import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ATTACHMENT_ACTION, REQUEST_ACTION, TABLE_INFO } from './app.action';
import { forkJoin, of } from 'rxjs';
import Store from '.';
import { HotToastService } from '@ngxpert/hot-toast';
import { API } from '@mysrc/constants/api.constant';
import {
  TableInformation,
  CompleteTableInformation,
} from '@mysrc/models/approval-hub.model';
import {
  withLatestFrom,
  filter,
  exhaustMap,
  map,
  switchMap,
  catchError,
  mergeMap,
} from 'rxjs/operators';
import { BaseHttpService, UserService } from '../modules/fwcore';
import { HttpClient } from '@angular/common/http';
import { OktaWrapperService } from '../../services/framework';

@Injectable()
export class AppEffects extends BaseHttpService {
  private readonly loggedInUserEmail: any = 'Client_demo@greentree.com'; //'Client_demo@greentree.com';
  constructor(
    @Inject('CONFIG_SETTINGS')
    private readonly configSettings: any,
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly toast: HotToastService,
    private readonly userService: UserService,
    private wrapper: OktaWrapperService,
    readonly http: HttpClient
  ) {
    super(http);
    //console.log(userService.getCurrentUser().subscribe((r) => { alert(JSON.stringify(r)) }))
    //const user = wrapper.getUser();
    //if (localStorage['userEmail']) {
    //this.loggedInUserEmail = localStorage['userEmail'];
    //}
  }

  $fetchTableDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(TABLE_INFO.tableDetails),
      withLatestFrom(this.store.categories$, this.wrapper.currentUser$),
      filter(([_, cats]) => cats.length == 0),
      exhaustMap(([_, _d, curUser]) =>
        this.getItemWithQuery<TableInformation[]>(
          this.configSettings.general.gxURL + API.APPROVAL_HUB,
          {
            params: {
              action: 'initialLoad',
              email: curUser?.email || '',
            },
            headers: this.getHttpBasicHeaders(this.getToken() || ''),
          }
        ).pipe(
          map((data: any) =>
            TABLE_INFO.tableDetailsSuccess({ data: data['result'] })
          )
        )
      )
    )
  );

  $triggerOtherTableDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(TABLE_INFO.tableDetailsSuccess),
      mergeMap(({ data }) =>
        Object.entries(
          data.reduce((acc, val, index) => {
            if (!acc[val.type]) {
              acc[val.type] = [index];
            } else {
              acc[val.type].push(index);
            }
            return acc;
          }, {} as { [category: string]: number[] })
        ).map(([category, tableIndexes]) =>
          TABLE_INFO.loadAllCategories({ category, tableIndexes })
        )
      )
    )
  );

  $fetchOtherTableDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(TABLE_INFO.loadAllCategories),
      withLatestFrom(this.wrapper.currentUser$),
      mergeMap(([{ category, tableIndexes }, curUser]) =>
        forkJoin(
          tableIndexes.map((index) =>
            this.getItemWithQuery<CompleteTableInformation>(
              this.configSettings.general.gxURL + API.APPROVAL_HUB,
              {
                params: {
                  action: 'tableSelected',
                  action_details: JSON.stringify({
                    tableData: index,
                  }),
                  email: curUser?.email || '',
                },
                headers: this.getHttpBasicHeaders(this.getToken() || ''),
              }
            ).pipe(map((d: any) => d.result))
          )
        ).pipe(
          map((data) =>
            TABLE_INFO.fetchConfigSuccess({
              data: data,
              name: category,
            })
          )
        )
      )
    )
  );

  $fetchTableConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(TABLE_INFO.selectCategory),
      withLatestFrom(this.store.subCategories$, this.wrapper.currentUser$),
      switchMap(([_, cats, curUser]) =>
        forkJoin(
          cats
            .filter(({ data }) => !data || data.length == 0)
            .map(({ index }) =>
              this.getItemWithQuery<CompleteTableInformation>(
                this.configSettings.general.gxURL + API.APPROVAL_HUB,
                {
                  params: {
                    action: 'tableSelected',
                    action_details: JSON.stringify({
                      tableData: index,
                    }),
                    email: curUser?.email || '',
                  },
                  headers: this.getHttpBasicHeaders(this.getToken() || ''),
                }
              )
            )
        ).pipe(
          map((data: any) =>
            TABLE_INFO.fetchConfigSuccess({
              data: data.map((d: any) => d.result),
            })
          )
        )
      )
    )
  );

  $approveRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(REQUEST_ACTION.approveRequest),
      withLatestFrom(this.wrapper.currentUser$),
      switchMap(([formData, curUser]) =>
        this.getItemWithQuery(
          this.configSettings.general.gxURL + API.APPROVAL_HUB,
          {
            params: {
              additionalDetails: JSON.stringify({
                ...formData.data,
                userEmail: curUser?.email || '',
              }),
              action: JSON.stringify(['approve']),
              email: JSON.stringify([curUser?.email || '']),
            },
            headers: this.getHttpBasicHeaders(this.getToken() || ''),
          }
        ).pipe(
          this.toast.observe({
            loading: 'Saving...',
            success: 'Approved!',
            error: 'Could not update.',
          }),
          map((res) => REQUEST_ACTION.approveRequestSuccess(res)),
          catchError((err) =>
            of(REQUEST_ACTION.approveRequestFailure({ message: err }))
          )
        )
      )
    )
  );

  $rejectRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(REQUEST_ACTION.rejectRequest),
      withLatestFrom(this.wrapper.currentUser$),
      switchMap(([formData, curUser]) =>
        this.getItemWithQuery(
          this.configSettings.general.gxURL + API.APPROVAL_HUB,
          {
            params: {
              additionalDetails: JSON.stringify({
                ...formData.data,
                userEmail: curUser?.email || '',
              }),
              action: JSON.stringify(['reject']),
              email: JSON.stringify([curUser?.email || '']),
            },
            headers: this.getHttpBasicHeaders(this.getToken() || ''),
          }
        ).pipe(
          this.toast.observe({
            loading: 'Saving...',
            success: 'Rejected!',
            error: 'Could not update.',
          }),
          map((res) => REQUEST_ACTION.rejectRequestSuccess(res)),
          catchError((err) =>
            of(REQUEST_ACTION.rejectRequestFailure({ message: err }))
          )
        )
      )
    )
  );

  $fetchAttachments = createEffect(() =>
    this.actions$.pipe(
      ofType(ATTACHMENT_ACTION.fetchAttachments),
      withLatestFrom(this.store.activeSubCategory$, this.wrapper.currentUser$),
      switchMap(([{ sysId }, activeCat, curUser]) =>
        this.getItemWithQuery<any[]>(
          this.configSettings.general.gxURL + API.APPROVAL_HUB,
          {
            params: {
              action: 'getAttachmentRecord',
              action_details: JSON.stringify([
                {
                  tableWholeData: activeCat?.metaData?.table_name,
                  sys_id: sysId,
                },
              ]),
              email: JSON.stringify([curUser?.email || '']),
            },
            headers: this.getHttpBasicHeaders(this.getToken() || ''),
          }
        ).pipe(
          map((attachments) =>
            ATTACHMENT_ACTION.fetchAttachmentsSuccess({ attachments })
          )
        )
      )
    )
  );

  $fetchAttachmentsa = createEffect(() =>
    this.actions$.pipe(
      ofType(ATTACHMENT_ACTION.fetchAttachments),
      withLatestFrom(this.store.activeSubCategory$),
      switchMap(([{ sysId }, activeCat]) =>
        this.getItemWithQuery<any[]>(
          this.configSettings.general.gxURL + API.ATTACHMENT,
          {
            params: {
              sysparm_query: `table_name=${activeCat?.metaData?.table_name}^table_sys_id=${sysId}`,
            },
            headers: this.getHttpBasicHeaders(this.getToken() || ''),
          }
        ).pipe(
          map((attachments) =>
            ATTACHMENT_ACTION.fetchAttachmentsSuccess({ attachments })
          )
        )
      )
    )
  );

  protected getToken(): string | undefined {
    const { general } = this.configSettings;
    return btoa(`${general.gxUserName}:${general.gxKey}`);
  }
}
